import type { Fragment } from '../types/fragments.types';
import type {
  FragmentLifecycleEventPayload,
  FragmentUpdateEventPayload,
} from '../types/events.types';

import type { FragmentStatus } from '../types/fragments.types';

import type {
  FragmentFormInputOptionKey,
  FragmentFormInputOptionValueType,
  FragmentFormInputOptions,
} from '../types/fragments.types';
import { FRAGMENT_FORM_INPUT_OPTION_TYPES } from '../constants/fragments.constants';

/**
 * Returns true when `key` is a recognised FragmentFormInputOptionKey.
 */
export function isFragmentFormInputOptionKey(key: string): key is FragmentFormInputOptionKey {
  return key in FRAGMENT_FORM_INPUT_OPTION_TYPES;
}

/**
 * Returns true when `value` is the correct runtime JS type for the given option key.
 *
 * Supports `boolean`, `string`, and `number` option types. Numeric values must be
 * finite (`NaN` and `Infinity` return false).
 */
export function isValidFragmentFormInputOptionValue<K extends FragmentFormInputOptionKey>(
  key: K,
  value: unknown,
): value is NonNullable<FragmentFormInputOptions[K]> {
  const expectedType = FRAGMENT_FORM_INPUT_OPTION_TYPES[key];

  switch (expectedType) {
    case 'boolean':
      return typeof value === 'boolean';
    case 'number':
      return typeof value === 'number' && Number.isFinite(value);
    case 'string':
      return typeof value === 'string';
    default:
      return false;
  }
}

/**
 * Parses a raw URL query-param string into the typed value expected by the option key.
 *
 * - `boolean` — accepts `"true"` / `"false"` (case-insensitive).
 * - `number`  — coerces any finite numeric string; returns `undefined` for non-numeric input.
 * - `string`  — returns the raw value as-is.
 *
 * Returns `undefined` when `rawValue` is `null` or cannot be parsed into the expected type.
 */
export function parseFragmentFormInputOptionValue<K extends FragmentFormInputOptionKey>(
  key: K,
  rawValue: string | null,
): FragmentFormInputOptions[K] | undefined {
  if (rawValue === null) {
    return undefined;
  }

  const expectedType = FRAGMENT_FORM_INPUT_OPTION_TYPES[key];

  switch (expectedType) {
    case 'boolean': {
      const normalized = rawValue.trim().toLowerCase();
      if (normalized === 'true') return true as FragmentFormInputOptions[K];
      if (normalized === 'false') return false as FragmentFormInputOptions[K];
      return undefined;
    }
    case 'number': {
      const parsed = Number(rawValue.trim());
      if (!Number.isFinite(parsed)) return undefined;
      return parsed as unknown as FragmentFormInputOptions[K];
    }
    case 'string': {
      return String(rawValue) as unknown as FragmentFormInputOptions[K];
    }
    default:
      return undefined;
  }
}

/**
 * Returns the expected value type for the given option key.
 */
export function getFragmentFormInputOptionType(
  key: FragmentFormInputOptionKey,
): FragmentFormInputOptionValueType {
  return FRAGMENT_FORM_INPUT_OPTION_TYPES[key];
}

/**
 * Returns true when a fragment status counts as valid/ready.
 *
 * Both `staged` and `submitted` are treated as valid because a submitted
 * fragment has already passed the staged state and its value is locked.
 */
export function isValidStatus(status: FragmentStatus): boolean {
  return status === 'staged' || status === 'submitted';
}

/**
 * Builds a lifecycle event payload from a fragment reference.
 *
 * Used for fragment-mounting, fragment-pending, fragment-staged, and fragment-error events.
 */
export function generateFragmentLifecycleContext(
  fragment: Fragment,
): FragmentLifecycleEventPayload {
  return {
    fragmentName: fragment.fragmentName,
    fragmentId: fragment.id,
    status: fragment.status,
  };
}

/**
 * Builds an update event payload from a fragment reference.
 *
 * `isValid` is derived from the fragment's current status using `isValidStatus`.
 */
export function generateFragmentUpdateContext(fragment: Fragment): FragmentUpdateEventPayload {
  return {
    fragmentName: fragment.fragmentName,
    status: fragment.status,
    isValid: isValidStatus(fragment.status),
    values: fragment.values,
    error: fragment.error,
  };
}
